import React, { ChangeEvent, Reducer, useMemo, useReducer } from "react";

import { Box, Button, CardMedia, Fab, TextField, Typography } from "@material-ui/core";

import clsx from "clsx";

import MenuItem from "api/types/MenuItem";
import ScrollBars from "components/ScrollBars";
import DesktopContainerDrawer from "containers/DesktopContainerDrawer";
import MobileContainerDrawer from "containers/MobileContainerDrawer";
import calculateMenuItemUnitPriceCents from "helpers/calculateMenuItemUnitPriceCents";
import makeStyles from "hooks/makeStyles";
import useFormatMoney from "hooks/useFormatMoney";
import useLocalize from "hooks/useLocalize";
import useTranslation from "hooks/useTranslation";

import MenuIcon from "icons/MenuIcon";

import MenuItemOptionGroups from "./MenuItemOptionGroups";
import SelectedOption from "./SelectedOption";

const useStyles = makeStyles(theme => ({
	closeButton: {
		marginLeft: -2,
		marginTop: -2,
		padding: 0,
		textAlign: "left",
		alignSelf: "flex-start"
	},
	closeIcon: {
		width: 36,
		height: 36,
		color: "#fff"
	},
	body: {
		height: "auto",
		padding: 0,
		margin: 0,
		[theme.breakpoints.up(480)]: {
			width: "100%",
			height: "auto",
			backgroundColor: theme.palette.background.default,
			color: theme.palette.text.primary,
			borderBottomLeftRadius: theme.spacing(2),
			borderBottomRightRadius: theme.spacing(2)
		}
	},
	availabilityWarningRow: {
		display: "flex",
		width: "100%",
		flexDirection: "row",
		justifyContent: "flex-start",
		alignContent: "center",
		alignItems: "start",
		margin: 0,
		padding: `${theme.spacing(1)}px ${theme.spacing(1)}px 0 ${theme.spacing(1)}px`
	},
	[`@media (min-width: ${DesktopContainerDrawer.minWidth}px)`]: {
		availabilityWarningRow: {
			margin: `${theme.spacing(1)}px 0 ${-theme.spacing(2)}px 0`,
			padding: `0 ${theme.spacing(2)}px 0 ${theme.spacing(2)}px`
		}
	},
	availabilityWarning: {
		fontSize: "15px",
		color: theme.palette.warning.main
	},
	titleRow: {
		display: "flex",
		width: "100%",
		flexDirection: "row",
		justifyContent: "flex-start",
		alignContent: "center",
		alignItems: "start",
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
		padding: `0 ${theme.spacing(2)}px`,
		margin: `${theme.spacing(2)}px 0`,
		[theme.breakpoints.up(480)]: {
			background: "#fff",
			color: theme.palette.text.primary,
			padding: `0 ${theme.spacing(4)}px`,
			margin: `${theme.spacing(2)}px 0`
		}
	},
	itemsRow: {
		display: "flex",
		width: "100%",
		flexDirection: "column",
		justifyContent: "flex-start",
		alignContent: "center",
		alignItems: "start",
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
		padding: `0 ${theme.spacing(2)}px`,
		margin: `${theme.spacing(2)}px 0`,
		[theme.breakpoints.up(480)]: {
			background: "#fff",
			color: theme.palette.text.primary,
			padding: `0 ${theme.spacing(4)}px`,
			margin: `${theme.spacing(2)}px 0`
		}
	},
	descriptionRow: {
		display: "flex",
		width: "100%",
		flexDirection: "row",
		justifyContent: "flex-start",
		alignContent: "center",
		alignItems: "start",
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
		padding: `0 ${theme.spacing(2)}px`,
		[theme.breakpoints.up(480)]: {
			background: "#fff",
			color: theme.palette.text.primary,
			padding: `0 ${theme.spacing(4)}px`,
			width: "100%",
			flexDirection: "row",
			justifyContent: "flex-start",
			alignContent: "center",
			alignItems: "center"
		}
	},
	instructionsRow: {
		display: "flex",
		width: "100%",
		flexDirection: "row",
		justifyContent: "flex-start",
		alignContent: "center",
		alignItems: "center",
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
		padding: `0 ${theme.spacing(2)}px`,
		[theme.breakpoints.up(480)]: {
			flexDirection: "column",
			background: "#fff",
			color: theme.palette.text.primary,
			marginTop: theme.spacing(1),
			marginBottom: theme.spacing(1),
			padding: `0 ${theme.spacing(4)}px`
		}
	},
	icon: {
		marginTop: 1,
		width: 24,
		marginRight: theme.spacing(1),
		paddingTop: theme.spacing(0.5),
		minWidth: theme.spacing(2),
		minHeight: theme.spacing(3),
		[theme.breakpoints.up(480)]: {
			display: "none"
		}
	},
	itemCode: {
		marginRight: theme.spacing(1),
		[theme.breakpoints.up(480)]: {
			fontSize: "18px"
		}
	},
	title: {
		alignSelf: "flex-start",
		textAlign: "left",
		textTransform: "uppercase",
		fontWeight: 500,
		fontSize: "1rem",
		letterSpacing: "0.38px",
		[theme.breakpoints.up(480)]: {
			fontSize: "18px"
		}
	},
	desc: {
		alignSelf: "flex-start",
		textAlign: "left",
		marginTop: theme.spacing(0.5),
		marginBottom: theme.spacing(1),
		fontWeight: 400,
		fontSize: 14
	},
	scrollContent: {
		display: "flex",
		flexDirection: "column",
		position: "relative",
		paddingBottom: 80
	},
	scrollBox: {
		height: "calc(var(--vh, 1vh) * 85 - 4px)", // TODO:  improve this
		overflowX: "auto",
		overflowY: "scroll",
		paddingTop: theme.spacing(4),
		[theme.breakpoints.up(480)]: {
			height: "auto",
			maxHeight: "calc(var(--vh, 1vh) * 85 - 4px)", // TODO:  improve this
			display: "block",
			width: "100%",
			padding: 0,
			overflowY: "scroll",
			overflowX: "hidden",
			marginBottom: "185px",
			paddingBottom: 120
		}
	},
	scrollBoxWithImage: {
		height: "calc(var(--vh, 1vh) * 85 - 4px)",
		paddingTop: 0,
		[theme.breakpoints.up(480)]: {
			maxHeight: "calc(var(--vh, 1vh) * 85 - 4px)"
		}
	},
	"@global": {
		"*::-webkit-scrollbar": {
			[theme.breakpoints.up(480)]: {
				width: "0px",
				hight: "0px"
			}
		}
	},
	textLabel: {
		color: theme.palette.text.primary
	},
	instructions: {
		display: "flex",
		flexDirection: "column",
		justifyContent: "flex-start"
	},
	instructionsField: {
		width: "calc(100vw - 32px)",
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(1.5),
		marginLeft: theme.spacing(2),
		marginRight: theme.spacing(2),
		padding: theme.spacing(0.5),
		borderColor: "#fff",
		color: "#000",
		backgroundColor: "#f2f2f2",
		borderRadius: 8,
		[theme.breakpoints.up(480)]: {
			display: "block",
			height: "auto",
			padding: theme.spacing(0.5),
			margin: theme.spacing(1),
			width: "100%",
			overflow: "auto"
		}
	},
	instructionsFieldRoot: {
		padding: 0,
		"&:before": {
			content: "none !important"
		},
		"&:after": {
			content: "none !important"
		}
	},
	instructionsInput: {
		justifyContent: "center",
		padding: theme.spacing(1)
	},
	quantity: {
		display: "flex",
		// width: "60vw",
		flexDirection: "row",
		justifyContent: "center",
		alignContent: "center",
		alignItems: "center",
		alignSelf: "center",
		marginTop: theme.spacing(2),
		[theme.breakpoints.up(480)]: {
			margin: theme.spacing(2),
			background: "#fff",
			borderRadius: "8px",
			height: theme.spacing(6)
		}
	},
	quantityButton: {
		backgroundColor: theme.palette.primary.main,
		fontFamily: "Arial, Helvetica, sans-serif",
		color: theme.palette.primary.contrastText,
		display: "flex",
		justifyContent: "center",
		justifyItems: "center",
		alignContent: "center",
		alignItems: "center",
		fontSize: 30,
		"&:disabled": {
			backgroundColor: "#aaa",
			color: theme.palette.primary.contrastText,
			[theme.breakpoints.up(480)]: {
				border: 0
			}
		},
		[theme.breakpoints.up(480)]: {
			border: 0,
			padding: 0,
			height: "100%",
			"&:hover": {
				backgroundColor: theme.palette.primary.main,
				color: theme.palette.text.primary
			}
		}
	},
	imageContainer: {
		width: "100%",
		height: "240px"
	},
	imageCard: {
		height: "100%"
	},
	quantityText: {
		display: "flex",
		justifyContent: "center",
		justifyItems: "center",
		alignContent: "center",
		alignItems: "center",
		width: 60,
		height: 52,
		fontSize: 24,
		color: "#fff",
		[theme.breakpoints.up(480)]: {
			border: 0,
			color: theme.palette.text.primary
		}
	},
	footer: {
		backgroundColor: "transparent",
		bottom: 0,
		left: 0,
		right: 0,
		width: "100%",
		marginTop: theme.spacing(1),
		marginRight: theme.spacing(1),
		marginBottom: theme.spacing(2),
		position: "absolute",
		[theme.breakpoints.up(480)]: {
			display: "flex",
			flexDirection: "row",
			background: "#4F4F4F"
		}
		// [theme.breakpoints.up(480)]: {
		// 	position: "initial",
		// 	marginTop: 0,
		// 	width: "auto",
		// 	flexDirection: "column",
		// 	flex: "1",
		// 	justifyContent: "center",
		// 	padding: theme.spacing(2)
		// }
	},
	submitButton: {
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
		borderRadius: 24,
		fontSize: 16,
		height: 48,
		width: "calc(100vw - 32px)",
		padding: 0,
		display: "flex",
		justifyContent: "center",
		justifyItems: "center",
		alignContent: "center",
		alignItems: "center",
		textTransform: "uppercase",
		marginLeft: theme.spacing(2),
		marginRight: theme.spacing(2),
		[theme.breakpoints.up(480)]: {
			margin: theme.spacing(2)
		},
		"&:hover": {
			[theme.breakpoints.up(480)]: {
				color: theme.palette.text.primary
			}
		}
	},
	actionButtonLabel: {
		marginLeft: theme.spacing(0.5)
	},
	outOfStock: {}
}));

type State = {
	menuItem: MenuItem;
	specialInstructions: string;
	quantity: number;
	selectedOptions: SelectedOption[];
	unitPriceCents: number;
	extendedPriceCents: number;
	valid: boolean;
};

type Action =
	| { type: "SET_INSTRUCTIONS"; payload: string }
	| { type: "INCREASE_QUANTITY" }
	| { type: "DECREASE_QUANTITY" }
	| {
			type: "SET_SELECTED_GROUP_OPTIONS";
			payload: SelectedOption[];
	  };

const isValid = (state: State): boolean => {
	if (state.menuItem.outOfStock) return false;

	if (state.quantity < 1) {
		return false;
	}

	if (state.menuItem?.menuItemOptionGroups.length) {
		for (const group of state.menuItem?.menuItemOptionGroups) {
			const selected = state.selectedOptions.filter(x => x.menuItemOptionGroupId === group.id);

			if (group.minCount && selected.length < group.minCount) {
				return false;
			}

			if (group.maxCount && selected.length > group.maxCount) {
				return false;
			}
		}
	}

	return true;
};

const validate = (state: State): State => ({ ...state, valid: isValid(state) });

const calculate = (state: State): State => {
	const unitPriceCents = calculateMenuItemUnitPriceCents(state);
	return { ...state, unitPriceCents, extendedPriceCents: unitPriceCents * state.quantity };
};

const calculateAndValidate = (state: State) => validate(calculate(state));

const initializer = ({
	menuItem,
	defaultSpecialInstructions,
	defaultQuantity,
	defaultSelectedOptions
}: {
	menuItem: State["menuItem"];
	defaultSpecialInstructions?: State["specialInstructions"];
	defaultQuantity?: State["quantity"];
	defaultSelectedOptions?: State["selectedOptions"];
}) =>
	calculateAndValidate({
		menuItem,
		specialInstructions: defaultSpecialInstructions || "",
		quantity: defaultQuantity || 1,
		selectedOptions: defaultSelectedOptions || [],
		unitPriceCents: 0,
		extendedPriceCents: 0,
		valid: false
	});

const reducer: Reducer<State, Action> = (state: State, action: Action) => {
	switch (action.type) {
		case "SET_INSTRUCTIONS":
			return validate({
				...state,
				specialInstructions: action.payload
			});

		case "INCREASE_QUANTITY":
			return calculateAndValidate({
				...state,
				quantity: state.quantity + 1
			});

		case "DECREASE_QUANTITY":
			return calculateAndValidate({
				...state,
				quantity: Math.max(1, state.quantity - 1)
			});

		case "SET_SELECTED_GROUP_OPTIONS":
			return calculateAndValidate({
				...state,
				selectedOptions: action.payload
			});

		default:
			return state;
	}
};

export interface MenuItemEditorProps {
	menuItem: MenuItem;
	itemAvailable: boolean;
	defaultSpecialInstructions?: string;
	defaultQuantity?: number;
	defaultSelectedOptions?: {
		menuItemOptionId: string;
		menuItemOptionGroupId?: string;
	}[];
	submitLabel?: string;
	onSubmit: (event: { state: State }) => void;
}

const MenuItemEditor = ({
	menuItem,
	itemAvailable,
	defaultSpecialInstructions,
	defaultQuantity,
	defaultSelectedOptions,
	submitLabel,
	onSubmit
}: MenuItemEditorProps) => {
	const { localize } = useLocalize();
	const { t } = useTranslation();
	const { formatMoney } = useFormatMoney();
	const classes = useStyles();

	const [state, dispatch] = useReducer(
		reducer,
		{ menuItem, defaultSpecialInstructions, defaultQuantity, defaultSelectedOptions },
		initializer
	);

	const { specialInstructions, quantity, selectedOptions, extendedPriceCents, valid } = state;

	const setInstructions = useMemo(() => (value: string) => dispatch({ type: "SET_INSTRUCTIONS", payload: value }), [
		dispatch
	]);

	const increaseQuantity = useMemo(() => () => dispatch({ type: "INCREASE_QUANTITY" }), [dispatch]);

	const decreaseQuantity = useMemo(() => () => dispatch({ type: "DECREASE_QUANTITY" }), [dispatch]);

	const setSelectedOptions = useMemo(
		() => (payload: SelectedOption[]) => dispatch({ type: "SET_SELECTED_GROUP_OPTIONS", payload }),
		[dispatch]
	);

	const handleInstructionsChange = useMemo(
		() => (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
			setInstructions(event.target.value);
		},
		[setInstructions]
	);

	const handleOptionsChange = useMemo(
		() => ({ value }: { value: SelectedOption[] }) => {
			setSelectedOptions(value);
		},
		[setSelectedOptions]
	);

	const handleSubmitButtonClick = useMemo(
		() => () => {
			if (isValid(state)) {
				onSubmit && onSubmit({ state });
			}
		},
		[state, onSubmit]
	);

	if (!menuItem) {
		return null;
	}

	return (
		<>
			<Box className={classes.body}>
				<DesktopContainerDrawer>
					<ScrollBars className={clsx(classes.scrollBox, { [classes.scrollBoxWithImage]: menuItem.imageUrl })}>
						<Box className={classes.scrollContent}>
							{menuItem.imageUrl && (
								<Box className={classes.imageContainer}>
									<CardMedia image={menuItem.imageUrl} className={classes.imageCard}></CardMedia>
								</Box>
							)}
							{itemAvailable ? null : (
								<Box className={classes.availabilityWarningRow}>
									<Typography className={classes.availabilityWarning}>
										This item is not available at the time you selected
									</Typography>
								</Box>
							)}
							<Box className={classes.titleRow}>
								<MenuIcon className={classes.icon} />
								{menuItem.itemCode && <Typography className={classes.itemCode}>{menuItem.itemCode}</Typography>}
								<Typography className={classes.title}>{localize(menuItem.localeNames, menuItem.name)}</Typography>
							</Box>
							<Box className={classes.descriptionRow}>
								<Typography className={classes.desc}>{menuItem.description}</Typography>
							</Box>
							{!menuItem?.menuItemOptionGroups?.length ? null : (
								<Box className={classes.itemsRow}>
									<MenuItemOptionGroups
										menuItemOptionGroups={menuItem.menuItemOptionGroups}
										defaultValue={selectedOptions}
										onChange={handleOptionsChange}
									/>
								</Box>
							)}
							{!menuItem?.disableSpecialInstructions && (
								<Box className={classes.instructionsRow}>
									<Typography className={classes.title}>{t("specialInstructions")}</Typography>
									<TextField
										fullWidth
										className={classes.instructionsField}
										InputProps={{
											className: classes.instructionsInput,
											classes: {
												root: classes.instructionsFieldRoot
											}
										}}
										inputProps={{ className: classes.instructionsInput, maxLength: 200 }}
										value={specialInstructions}
										onChange={handleInstructionsChange}
										multiline
									/>
								</Box>
							)}
						</Box>
					</ScrollBars>
				</DesktopContainerDrawer>
				<MobileContainerDrawer>
					<Box className={clsx(classes.scrollBox, { [classes.scrollBoxWithImage]: menuItem.imageUrl })}>
						<Box className={classes.scrollContent}>
							{menuItem.imageUrl && (
								<Box className={classes.imageContainer}>
									<CardMedia image={menuItem.imageUrl} className={classes.imageCard}></CardMedia>
								</Box>
							)}
							{itemAvailable ? null : (
								<Box className={classes.availabilityWarningRow}>
									<Typography className={classes.availabilityWarning}>
										This item is not available at the time you selected
									</Typography>
								</Box>
							)}
							<Box className={classes.titleRow}>
								<MenuIcon className={classes.icon} />
								{menuItem.itemCode && <Typography className={classes.itemCode}>{menuItem.itemCode}</Typography>}
								<Typography className={classes.title}>{localize(menuItem.localeNames, menuItem.name)}</Typography>
							</Box>
							<Box className={classes.descriptionRow}>
								<Typography className={classes.desc}>{menuItem.description}</Typography>
							</Box>
							{!menuItem?.menuItemOptionGroups?.length ? null : (
								<Box className={classes.itemsRow}>
									<MenuItemOptionGroups
										menuItemOptionGroups={menuItem.menuItemOptionGroups}
										defaultValue={selectedOptions}
										onChange={handleOptionsChange}
									/>
								</Box>
							)}
							<TextField
								fullWidth
								className={classes.instructionsField}
								InputProps={{
									className: classes.instructionsInput,
									classes: {
										root: classes.instructionsFieldRoot
									}
								}}
								inputProps={{ className: classes.instructionsInput, maxLength: 200 }}
								value={specialInstructions}
								onChange={handleInstructionsChange}
								multiline
								placeholder={t("specialInstructions")}
								//variant="outlined" // TODO: autosizing doesn't work with outlined variant
							/>

							<Box className={classes.quantity}>
								<Fab
									className={classes.quantityButton}
									onClick={decreaseQuantity}
									disabled={quantity === 1}
									color={"primary"}
								>
									−
								</Fab>

								<Typography className={classes.quantityText}>{quantity}</Typography>

								<Fab className={classes.quantityButton} onClick={increaseQuantity} color={"primary"}>
									+
								</Fab>
							</Box>
						</Box>
					</Box>
				</MobileContainerDrawer>
			</Box>
			<Box className={classes.footer}>
				<DesktopContainerDrawer>
					<Box className={classes.quantity}>
						<Button
							variant="outlined"
							className={classes.quantityButton}
							onClick={decreaseQuantity}
							disabled={quantity === 1}
						>
							−
						</Button>

						<Typography className={classes.quantityText}>{quantity}</Typography>

						<Button variant="outlined" className={classes.quantityButton} onClick={increaseQuantity}>
							+
						</Button>
					</Box>
				</DesktopContainerDrawer>
				{!menuItem.outOfStock && (
					<Button
						className={classes.submitButton}
						variant="outlined"
						disabled={!valid}
						onClick={handleSubmitButtonClick}
					>
						{formatMoney({ priceCents: extendedPriceCents })}
						{!submitLabel ? null : <Typography className={classes.actionButtonLabel}>{` - ${submitLabel}`}</Typography>}
					</Button>
				)}

				{menuItem.outOfStock && (
					<Button className={`${classes.submitButton} ${classes.outOfStock}`} variant="outlined" disabled>
						{t("-outOfStock-")}
					</Button>
				)}
			</Box>
		</>
	);
};

export default MenuItemEditor;
